"use server";

import { auth } from "@/auth";
import type { ActionResult } from "@/types/server-action";
import {
  saveDraftTradeInDataSchema,
  type SaveDraftTradeInDataInput,
} from "@/lib/schemas/cat/vehicle-trade-in-save-draft";
import { validateParams } from "@/lib/utils";
import { prisma } from "@/prisma";
import type {
  Draft,
  DraftTradeInVehicle,
  Prisma,
} from "@/prisma/generated/client";
import { updateDraftAfterStepSave } from "@/lib/cat/draft-step-save";
import {
  buildDraftLockHolder,
  DraftLockDeniedError,
} from "@/lib/cat/draft-lock";

export type DraftWithTradeInVehicles = Draft & {
  tradeInVehicles: DraftTradeInVehicle[];
};

const draftTradeInInclude = {
  tradeInVehicles: {
    orderBy: { sortOrder: "asc" as const },
  },
} satisfies Prisma.DraftInclude;

async function assertDraftExists(draftId: number): Promise<void> {
  const draft = await prisma.draft.findUnique({
    where: { id: draftId },
    select: { id: true },
  });

  if (!draft) {
    throw new Error("Brouillon introuvable.");
  }
}

async function assertActiveBrandsExist(brandIds: number[]): Promise<void> {
  if (brandIds.length === 0) {
    return;
  }

  const uniqueBrandIds = [...new Set(brandIds)];
  const activeBrandCount = await prisma.vehicleBrand.count({
    where: {
      id: { in: uniqueBrandIds },
      isActive: true,
      deletedAt: null,
    },
  });

  if (activeBrandCount !== uniqueBrandIds.length) {
    throw new Error("Une ou plusieurs marques sélectionnées sont invalides.");
  }
}

async function syncTradeInVehicles(
  tx: Prisma.TransactionClient,
  draftId: number,
  tradeInVehicles: Extract<
    SaveDraftTradeInDataInput,
    { hasTradeIn: true }
  >["tradeInVehicles"],
): Promise<number[]> {
  const keptIds: number[] = [];

  for (let index = 0; index < tradeInVehicles.length; index += 1) {
    const vehicle = tradeInVehicles[index];

    if (vehicle.id !== undefined) {
      const updated = await tx.draftTradeInVehicle.updateMany({
        where: { id: vehicle.id, draftId },
        data: {
          brandId: vehicle.brandId,
          model: vehicle.model,
          sortOrder: index,
        },
      });

      if (updated.count === 0) {
        throw new Error("Véhicule de reprise introuvable pour ce brouillon.");
      }

      keptIds.push(vehicle.id);
      continue;
    }

    const created = await tx.draftTradeInVehicle.create({
      data: {
        draftId,
        brandId: vehicle.brandId,
        model: vehicle.model,
        sortOrder: index,
      },
    });
    keptIds.push(created.id);
  }

  await tx.draftTradeInVehicle.deleteMany({
    where: {
      draftId,
      ...(keptIds.length > 0 ? { id: { notIn: keptIds } } : {}),
    },
  });

  return keptIds;
}

async function fetchDraftWithTradeInVehicles(
  draftId: number,
): Promise<DraftWithTradeInVehicles | null> {
  return prisma.draft.findUnique({
    where: { id: draftId },
    include: draftTradeInInclude,
  });
}

export async function saveDraftTradeInData(
  params: SaveDraftTradeInDataInput,
): Promise<ActionResult<DraftWithTradeInVehicles>> {
  try {
    const session = await auth();
    if (!session?.user?.id) {
      return { success: false, message: "Non authentifié." };
    }
    const payload = validateParams(saveDraftTradeInDataSchema, params);
    await assertDraftExists(payload.id);
    const sessionKvps =
      session.data?.kvps?.current?.identifier ?? String(payload.id);
    const lockHolder = buildDraftLockHolder(session.user);

    if (!payload.hasTradeIn) {
      await prisma.$transaction(async (tx) => {
        await tx.draftTradeInVehicle.deleteMany({
          where: { draftId: payload.id },
        });
      });
    } else {
      await assertActiveBrandsExist(
        payload.tradeInVehicles.map((vehicle) => vehicle.brandId),
      );

      await prisma.$transaction(async (tx) => {
        await syncTradeInVehicles(tx, payload.id, payload.tradeInVehicles);
      });
    }

    await updateDraftAfterStepSave({
      draftId: payload.id,
      stepId: "vehicle_trade-in",
      userId: lockHolder.userId,
      userName: lockHolder.userName,
      kvps: sessionKvps,
      data: { hasTradeIn: payload.hasTradeIn },
    });

    const savedDraft = await fetchDraftWithTradeInVehicles(payload.id);
    if (!savedDraft) {
      return { success: false, message: "Brouillon introuvable." };
    }

    return { success: true, data: savedDraft };
  } catch (error) {
    console.error("[saveDraftTradeInData]", error);

    if (error instanceof DraftLockDeniedError) {
      return { success: false, message: error.message };
    }

    if (error instanceof Error && error.message.length > 0) {
      return { success: false, message: error.message };
    }

    return {
      success: false,
      message: "Une erreur serveur est survenue.",
    };
  }
}
